import { loginBodySchema, type LoginBody } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { ApiError } from '@/lib/api/client.js';
import { setCsrfToken } from '@/lib/api/csrf.js';
import { queryKeys } from '@/lib/query/keys.js';
import { login } from './api.js';

function readRedirectTarget(search: unknown): string {
  if (typeof search === 'object' && search !== null && 'redirect' in search) {
    const value = (search as { redirect?: unknown }).redirect;
    if (typeof value === 'string' && value.startsWith('/')) {
      return value;
    }
  }
  return '/';
}

/**
 * `02` §1 : email, mot de passe, bouton « Se connecter », lien « Mot de passe
 * oublié ». Le message de verrouillage après 5 échecs en 15 min est le
 * message exact renvoyé par l'API (`ApiError.message`, statut 429) — jamais
 * reformulé ici.
 */
export function LoginForm(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search: unknown = useSearch({ strict: false });
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginBody>({ resolver: zodResolver(loginBodySchema) });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (session) => {
      setCsrfToken(session.csrfToken);
      queryClient.setQueryData(queryKeys.auth.me(), session.user);
      void navigate({ to: readRedirectTarget(search) });
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Une erreur technique est survenue.',
      );
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => {
          setFormError(null);
          mutation.mutate(values);
        })(event);
      }}
    >
      <Field label="Email" htmlFor="email" error={errors.email?.message}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          aria-invalid={errors.email ? true : undefined}
          {...register('email')}
        />
      </Field>
      <Field label="Mot de passe" htmlFor="password" error={errors.password?.message}>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          aria-invalid={errors.password ? true : undefined}
          {...register('password')}
        />
      </Field>
      {formError ? (
        <p
          role="alert"
          className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
        >
          {formError}
        </p>
      ) : null}
      <Button type="submit" className="w-full" isLoading={isSubmitting || mutation.isPending}>
        Se connecter
      </Button>
      <Link
        to="/mot-de-passe-oublie"
        className="self-center rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        Mot de passe oublié
      </Link>
    </form>
  );
}
